import { AgGridReact } from "ag-grid-react";
import { ColDef, iconOverrides, themeQuartz } from "ag-grid-community";
import { observer } from "mobx-react-lite";
import { useRef, useEffect, useMemo } from "react";

import { BaseTableData, TableColumn, TableV2Props } from "./types";
import { TableV2GridContextProvider } from "./grid-store";
import {
  useDimensions,
  useColumnDefs,
  useRowHandlers,
  useGridReady,
  useGridOptions,
  useColumnsFit,
} from "./hooks";

import "./styles.css";

import { cn } from "@/lib/utils";

export const TableV2Internal = observer(
  <T extends BaseTableData>({
    gridRef,
    data,
    columns,
    isLoading,
    enableSorting = true,
    enableFiltering,
    enableResizing = true,
    enableRowSelection,
    enableMultiRowSelection,
    enablePagination,
    pageSize = 50,
    className,
    height,
    maxHeight,
    striped,
    hoverable = true,
    onRowClick,
    onRowDoubleClick,
    onSelectionChanged,
    onGridReady,
    emptyStateMessage = "No data available",
    emptyStateComponent,
    contextMenuItems,
    loadingComponent,
    shouldAutoSizeAllColumns,
    gridOptions: customGridOptions,
    currentPage,
    getRowId,
    rowClass,
    suppressHeaderMenuButton,
    isHighlighted,
    rowNumbers,
    autoFitRowsToHeight,
    ...tableProps
  }: Omit<TableV2Props<T>, "gridContextStorageKey" | "gridContextCustomActions">) => {
    const { rowHeight, setRowHeight, actualHeaderHeight, containerStyle } = useDimensions({
      hoverable,
    });

    const { columnDefs } = useColumnDefs<T>({
      columns,
      enableSorting,
      enableResizing,
      enableFiltering,
      suppressHeaderMenuButton,
      contextMenuItems,
    });

    const { gridOptions } = useGridOptions({
      hoverable,
      enablePagination,
      pageSize,
      enableRowSelection,
      enableMultiRowSelection,
      rowNumbers,
      emptyStateMessage,
      customGridOptions,
    });

    const { handleRowClick, handleSelectionChanged, getRowClass, getRowIdFunc } = useRowHandlers<T>(
      {
        onRowClick,
        onRowDoubleClick,
        onSelectionChanged,
        hoverable,
        striped,
        isHighlighted,
        getRowId,
      },
    );

    const { handleGridReady, gridContainerRef } = useGridReady<T>({
      gridRef,
      data,
      actualHeaderHeight,
      autoFitRowsToHeight,
      onGridReady,
      setRowHeight,
    });

    useColumnsFit<T>({
      gridRef,
      gridContainerRef,
    });

    const hasAutoSizedRef = useRef(false);

    useEffect(() => {
      if (shouldAutoSizeAllColumns && gridRef?.current?.api && !hasAutoSizedRef.current) {
        gridRef?.current?.api?.sizeColumnsToFit();
        hasAutoSizedRef.current = true;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldAutoSizeAllColumns, gridRef?.current?.api]);

    useEffect(() => {
      if (enablePagination && gridRef?.current?.api && currentPage && currentPage > 0) {
        const agGridCurrentPage = gridRef.current.api.paginationGetCurrentPage() + 1;
        if (agGridCurrentPage !== currentPage) {
          gridRef.current.api.paginationGoToPage(currentPage - 1);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enablePagination, currentPage, gridRef?.current?.api]);

    const defaultColDef: ColDef = useMemo(
      () => ({
        sortable: enableSorting,
        resizable: enableResizing,
        filter: enableFiltering,
      }),
      [enableSorting, enableResizing, enableFiltering],
    );

    const tableIconOverrides = iconOverrides({
      type: "image",
      icons: {
        paintBrush: {
          svg: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-brush-icon lucide-brush"><path d="m11 10 3 3"/><path d="M6.5 21A3.5 3.5 0 1 0 3 17.5a2.62 2.62 0 0 1-.708 1.792A1 1 0 0 0 3 21z"/><path d="M9.969 17.031 21.378 5.624a1 1 0 0 0-3.002-3.002L6.967 14.031"/></svg>',
        },
      },
    });

    const tableTheme = themeQuartz.withPart(tableIconOverrides);

    return (
      <div
        className={cn("pl-tablev2", className)}
        ref={gridContainerRef}
        style={
          {
            width: "100%",
            height: "100%",
            maxHeight: maxHeight,
            minHeight: "300px",
            paddingBottom: "16px",
            ...containerStyle,
          } as React.CSSProperties
        }
      >
        <AgGridReact<T>
          ref={gridRef}
          rowData={data}
          columnDefs={columnDefs}
          rowBuffer={25}
          defaultColDef={defaultColDef}
          theme={tableTheme}
          alwaysShowHorizontalScroll
          rowHeight={rowHeight}
          gridOptions={gridOptions}
          loading={isLoading}
          onRowClicked={handleRowClick}
          onSelectionChanged={handleSelectionChanged}
          onGridReady={handleGridReady}
          getRowClass={getRowClass}
          getRowId={getRowIdFunc}
          suppressContextMenu
          detailRowAutoHeight={false}
          maintainColumnOrder
          {...tableProps}
        />
      </div>
    );
  },
);

export const TableV2 = <T extends BaseTableData>(props: TableV2Props<T>) => {
  const {
    gridContextStorageKey,
    gridContextCustomActions,
    columns,
    defaultViewMode,
    ...restProps
  } = props;

  return (
    <TableV2GridContextProvider<TableColumn<T>>
      columns={columns}
      defaultViewMode={defaultViewMode}
      storageKey={gridContextStorageKey}
      getColumnId={column => String(column.id)}
      customActions={gridContextCustomActions}
    >
      <TableV2Internal<T> {...restProps} columns={columns} />
    </TableV2GridContextProvider>
  );
};

export type { BaseTableData, TableColumn, TableV2Props } from "./types";
export { ViewMode, useTableV2GridContext } from "./grid-store";
